import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime, formatLabel } from '@/lib/format'

export type AdminEntitlementRow = {
  id: string
  user_id: string
  plan: 'harmoni_plus'
  source: 'polar' | 'manual_grant' | 'comp' | 'trial' | 'honorarium'
  starts_at: string
  ends_at: string | null
  revoked_at: string | null
  note: string | null
  external_ref: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  email: string | null
  username: string | null
  display_name: string | null
  active: boolean
  status: 'active' | 'expired' | 'revoked' | 'scheduled'
}

export const ADMIN_ENTITLEMENT_SORT_IDS = [
  'createdAt',
  'startsAt',
  'endsAt',
  'source',
  'status',
] as const

export const ADMIN_ENTITLEMENT_FILTER_KEYS = ['status', 'source'] as const

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'All', value: 'all' },
] as const

const SOURCE_OPTIONS = [
  { label: 'Comp', value: 'comp' },
  { label: 'Trial', value: 'trial' },
  { label: 'Manual', value: 'manual_grant' },
  { label: 'Polar', value: 'polar' },
  { label: 'Honorarium', value: 'honorarium' },
] as const

function statusBadge(status: AdminEntitlementRow['status']) {
  const variant =
    status === 'active'
      ? 'default'
      : status === 'revoked'
        ? 'destructive'
        : 'secondary'
  return (
    <Badge variant={variant} className="font-normal capitalize">
      {status}
    </Badge>
  )
}

export function createAdminEntitlementsColumns(): ColumnDef<
  AdminEntitlementRow,
  unknown
>[] {
  return [
    {
      id: 'user',
      accessorFn: (r) => r.display_name ?? r.email ?? r.user_id,
      meta: { label: 'User' },
      enableSorting: false,
      enableHiding: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="User" />
      ),
      cell: ({ row }) => {
        const e = row.original
        const label = e.display_name ?? e.username ?? e.email ?? e.user_id
        return (
          <div className="min-w-0 max-w-[14rem]">
            <div className="truncate text-sm font-medium">{label}</div>
            <div className="truncate text-xs text-muted-foreground">
              {e.email ?? e.user_id.slice(0, 12)}
            </div>
          </div>
        )
      },
    },
    {
      id: 'source',
      accessorKey: 'source',
      meta: {
        label: 'Source',
        variant: 'select',
        options: [...SOURCE_OPTIONS],
      },
      enableSorting: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Source" />
      ),
      cell: ({ row }) => (
        <span className="text-sm">{formatLabel(row.original.source)}</span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      meta: {
        label: 'Status',
        variant: 'select',
        options: [...STATUS_OPTIONS],
      },
      enableSorting: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => statusBadge(row.original.status),
    },
    {
      id: 'startsAt',
      accessorKey: 'starts_at',
      meta: { label: 'Starts' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Starts" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.original.starts_at)}
        </span>
      ),
    },
    {
      id: 'endsAt',
      accessorKey: 'ends_at',
      meta: { label: 'Ends' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Ends" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {row.original.ends_at
            ? formatDateTime(row.original.ends_at)
            : 'Open-ended'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      accessorKey: 'created_at',
      meta: { label: 'Granted' },
      enableSorting: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Granted" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
  ]
}
